

import React, { useContext, useEffect } from "react"
import { useForm, FormProvider} from 'react-hook-form'
import * as yup from 'yup';
import { QuoteContextStore } from '../../../context/QuoteContext/QuoteContext';
import { Divider, Grid, useMediaQuery } from "@material-ui/core"
import { yupResolver } from '@hookform/resolvers/yup';
import LiabilitySection from "./CoveragesSections/LiabilitySection";
import { OrangeButton } from "../../Core/Buttons/OrangeButton";
import { FlowBackButton } from "../../Core/Buttons/FlowBackButton";
import MedicalSection from "./CoveragesSections/MedicalSection";
import './CoverageSelctionPage.scss'
import { MobileWidget } from '../PriceWidgets/MobileWidget';
import { SessionContextStore } from "../../../context/SessionContext/SessionContext";
import { useParams, useHistory } from 'react-router-dom';
import { QUOTE_STATUS } from "../../../context/QuoteContext/types";
import { AlertContextStore } from '../../../context/AlertContext/AlertContext';

export const coveragesSchema = yup
.object()
  .shape({
    start_date: yup.date().typeError('Required').required("Required")
    .test("three_months", "Start date must be within 3 months", (startDate) => {
        const now = new Date();
        const threeMonthsFromNow = new Date(now.setMonth(now.getMonth() + 3));
        return startDate <= threeMonthsFromNow;
    })
    .test("past_date", "Must not be past date", (startDate) => {
        const now = new Date();
        const nowDate = new Date(now.setHours(0,0,0,0));
        return startDate > nowDate;
    }),
    liability_coverage: yup.string().nullable().required("Required").test("state", "Required", (liability_coverage) => liability_coverage !== 'default'),
    has_physical_damage: yup.boolean(),
    physical_damage_coverage: yup.string().nullable()
    .when(['has_physical_damage'], (has_physical_damage, schema) => {
        return !has_physical_damage ? schema : schema.test("state", "Required", (physical_damage_coverage) => physical_damage_coverage !== 'default');
    }),
    add_airport_authority: yup.boolean(),
    airport_authority_name: yup.string("Required")
    .when(['add_airport_authority'], (add_airport_authority, schema) => {
        return add_airport_authority ? schema.typeError('Required').required("Required").test("checkForEmpty", "Required", (airport_authority_name) => airport_authority_name.trim() !== '') : schema;
    }),
    airport_authority_address1: yup.string("Required")
    .when(['add_airport_authority'], (add_airport_authority, schema) => {
        return add_airport_authority ? schema.typeError('Required').required("Required").test("checkForEmpty", "Required", (airport_authority_address1) => airport_authority_address1.trim() !== '') : schema;
    }),
    airport_authority_city:  yup.string("Required")
    .when(['add_airport_authority'], (add_airport_authority, schema) => {
        return add_airport_authority ? schema.typeError('Required').required("Required").test("checkForEmpty", "Required", (airport_authority_city) => airport_authority_city.trim() !== '') : schema;
    }),
    airport_authority_state: yup.string().nullable()
    .when(['add_airport_authority'], (add_airport_authority, schema) => {
        return add_airport_authority ? schema.test("state", "Required", (airport_authority_state) => airport_authority_state !== 'default') : schema;
    }),
    airport_authority_zipcode: yup.string("Required")
    .when(['add_airport_authority'], (add_airport_authority, schema) => {
        return add_airport_authority ? schema.typeError('Required').required("Required").test("checkForEmpty", "Required", (airport_authority_zipcode) => airport_authority_zipcode.trim() !== '') : schema;
    })
})
.required();

export const CoveragesStep = () => {
    const { quoteId } = useParams();
    const { showInfo } = useContext(AlertContextStore);
    const history = useHistory();
    const {status, finishCoverages, loadQuote, aircrafts,shouldShowMedicalCoverage, quoteLoaded, coverages: { selected }, loadOptions,getClearance, start_date, add_airport_authority, airport_authority, shouldShowPriceWidgit } = useContext(QuoteContextStore);
    const aircraft = aircrafts[Object.keys(aircrafts)[0]];
    const form = useForm({mode: 'onBlur' ,defaultValues: 
    { start_date: start_date, ...selected,
    airport_authority_address1: airport_authority?.address_line1 ?? '' ,
    airport_authority_name: airport_authority?.name ?? '',
    airport_authority_address2: airport_authority?.address_line2 ?? '',
    airport_authority_state: airport_authority?.state ?? "default",
    airport_authority_zipcode: airport_authority?.zip ?? '',
    airport_authority_city: airport_authority?.city ?? '',
    add_airport_authority: add_airport_authority ,
    has_physical_damage: aircraft.value > 0 }, resolver: yupResolver(coveragesSchema)});    
    const isTablet = useMediaQuery((theme) => theme.breakpoints.down('md'));
    
    useEffect(() => {
        form.register("has_physical_damage")
        return () => {
            form.unregister("has_physical_damage")
        }
    }, [form.register, form.unregister])

    useEffect(()=>{
        if(!quoteLoaded || !quoteId) return;
        if(status === QUOTE_STATUS.UNDERWRITER || status === QUOTE_STATUS.DECLINED) {
            showInfo({
                title: "Quote in Underwriter Review",
                message: "Our underwriters are currently reviewing your quote. If you make changes to the information, it will cancel the referral process. Are you sure you want to continue?",
                confirmButtonText: "Edit quote",
                cancelButtonText: "Start a new quote",
                onConfirm: async () => {
                getClearance(quoteId).then(res => {
                        if(!res) return;
                        loadOptions(quoteId);
                    })
                },
                onCancel: () => history.push(`/insurance/profile`),
            });
        } else {
            getClearance(quoteId).then(res => {
                if(!res) return;
                loadOptions(quoteId);
            });
        }
    }, [quoteLoaded, quoteId])

    useEffect(()=>{
        const prev = form.getValues();
        form.reset({ 
            ...selected,
            has_physical_damage: aircraft.value > 0,
            start_date: start_date,
            airport_authority_address1: airport_authority?.address_line1 ?? '' ,
            airport_authority_name: airport_authority?.name ?? '',
            airport_authority_address2: airport_authority?.address_line2 ?? '',
            airport_authority_state: airport_authority?.state ?? "default",
            airport_authority_zipcode: airport_authority?.zip ?? '',
            airport_authority_city: airport_authority?.city ?? '',
            add_airport_authority: add_airport_authority ,
        })
    }, [selected, start_date])

    const handleSubmit = async (data) => {
        await finishCoverages({...data, airport_authority_id: airport_authority?.id ?? null ,quote_id: quoteId})
        await loadQuote(quoteId);
    }
    
    return (
        <React.Fragment>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="margin-top-medium margin-bottom-basic">
                    <FormProvider {...form}>
                        <LiabilitySection />
                        <Divider />
                        {shouldShowMedicalCoverage && <MedicalSection medicalLimit={selected.medical_coverage}/>}
                        {isTablet && shouldShowPriceWidgit && <div className="margin-bottom-basic">
                            <Divider />
                            <MobileWidget />
                        </div>}
                        <Grid>
                            <FlowBackButton />
                            <OrangeButton type="submit">Save & Continue</OrangeButton>
                        </Grid>
                    </FormProvider>
                </form>
        </React.Fragment>
    )
}